import { useEffect, useState } from 'react';

import FabricSubCategory from '@components/commonFilters/materials/Fabrics/FabricSubCategory';
import CustomDropdown from '@components/CustomDropdown';
import { toastError } from '@components/Toast';
import AddNewFabricType from '@components/materials/AddNewFabricType';

import ApiClient from '@lib/apiClient';

import {
	compositionCalculation,
	compositionDefaultFieldSet,
	fetchCompositionNameList,
	getFabricCategory,
	onChangeCompositionPercentage,
	onChangeCompositionSelection
} from '@utils/MaterialsUtil';
import { checkAnyUserType } from '@utils/Util';

const AddFabricInline = ({
	materialType,
	fabricData,
	productId,
	existingId,
	onClose,
	onSuccess,
	className = ''
}) => {
	const [compositionNameList, setCompositionNameList] = useState([]);
	const [compositionFieldInfoList, setCompositionFieldInfoList] = useState(
		compositionDefaultFieldSet(2, [])
	);
	const [fabricDetails, setFabricDetails] = useState({});
	const [isFabricTypeModify, setIsFabricTypeModify] = useState(false);
	const [isAddNew, setIsAddNew] = useState(false);
	const [addNewValue, setAddNewValue] = useState('');
	const [fabricTypeList, setFabricTypeList] = useState([]);
	const [errors, setErrors] = useState({});
	const [errors2, setErrors2] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const [fabricTypeResponse, setFabricTypeResponse] = useState([]);
	const [selectedFabricTypeToEdit, setSelectedFabricTypeToEdit] = useState(null);

	useEffect(() => {
		fetchCompositionNameList(setCompositionNameList).finally();
	}, []);

	useEffect(() => {
		setFabricDetails(fabricData);
		if (fabricData?.compositionPartList !== undefined && fabricData?.compositionPartList.length > 0) {
			let modulus = fabricData?.compositionPartList.length % 2;
			let length =
				modulus === 0
					? fabricData?.compositionPartList.length
					: fabricData?.compositionPartList.length + 1;
			setCompositionFieldInfoList(compositionDefaultFieldSet(length, fabricData?.compositionPartList));
		}
	}, [fabricData]);

	const onAddNew = (value) => {
		setAddNewValue(value);
		getFabricCategory(setIsAddNew, setFabricTypeList);
	};

	const setNewFabricType = ({ data }) => {
		if (!data) {
			return;
		}
		setSelectedFabricTypeToEdit({ ...data });
		setFabricDetails({
			...fabricDetails,
			fabricType: data.fabricType,
			fabricTypeWithConstruction: `${data.fabricType}-${data.name}`,
			constructionId: data.id
		});
		setIsFabricTypeModify(true);
	};

	const isValidForm = (body) => {
		if (!body.fabricType) {
			setErrors2({ ...errors2, fabricTypeError: 'Fabric type is required' });
			return false;
		}

		if (body.gsm === null || body.gsm === undefined || body.gsm.toString().trim() === '') {
			setErrors({ ...errors, weightError: 'Weight is required' });
			return false;
		}
		return true;
	};

	const onChangeInputs = (e) => {
		if (e.target.name === 'compositionDetails') {
			setIsLoading(true);
		}
		setFabricDetails({
			...fabricDetails,
			[e.target.name]: e.target.value
		});
	};

	const compositionPartChecking = async (selected) => {
		if (selected)
			await ApiClient.GET('getCompositionParts', `?compositionDetails=` + encodeURIComponent(selected))
				.then(({ data }) => {
					if (data.length === 0) {
						setErrors({ ...errors, compositionError: 'Composition is wrong' });
					} else {
						setErrors({ ...errors, compositionError: undefined });
						fabricAdd(data);
					}
				})
				.catch(({ response }) => {
					if (response && response.data && response.data.message) {
						toastError(response.data.message);
						setErrors({ ...errors, compositionError: 'Composition is wrong' });
					} else {
						setErrors({ ...errors, compositionError: 'Composition is wrong' });
					}
				});
	};

	const onSubmitData = async () => {
		let compositionCalObj = compositionCalculation(compositionFieldInfoList);

		if (compositionCalObj.percentageIsNotOk) {
			setErrors({ ...errors, compositionError: 'Total composition needs to be 100%' });
			return false;
		} else if (compositionCalObj.fiberNameIsNotOk) {
			setErrors({ ...errors, compositionError: 'Select composition' });
			return false;
		} else {
			setErrors({ ...errors, compositionError: undefined });
			compositionPartChecking(compositionCalObj.compositionPart).finally(() => {});
		}
	};

	const fabricAdd = async (compositionPartIdList) => {
		try {
			const body = {
				materialType,
				productId,
				// compositionDetail: fabricDetails?.compositionDetails,
				fabricType: fabricDetails?.fabricType,
				gsm: fabricDetails?.weight,
				construction: fabricDetails?.constructionId,
				fabricCompositionPartIdList: compositionPartIdList,
				deleteExistingId: existingId
			};
			const isValid = isValidForm(body);
			if (!isValid) return;
			await ApiClient.POST('addFabric', body);
			setErrors({});
			onSuccess();
		} catch ({ response }) {
			if (response.data.message) {
				toastError(response.data.message);
			} else {
				toastError('Something went wrong! Please try again.');
			}
		}
	};

	const compositionRender = (obj, index) => {
		return (
			<div className="col-md-6" key={`composition-${index}`}>
				<div className="form-group composition-item mb-0">
					<div className="d-flex">
						<input
							type="number"
							// pattern={'[0-9]+(.[0-9]{1,2})?'}
							defaultValue={obj?.percentage}
							min={0.1}
							max={100}
							onBlur={(e) =>
								onChangeCompositionPercentage(
									e.target.value,
									index,
									compositionFieldInfoList,
									setCompositionFieldInfoList
								)
							}
							className="input-percentage"
						/>
						<p className="percentage">%</p>
						<div className="select-component">
							<div className="form-group">
								<CustomDropdown
									type="fabricName"
									title={'Composition'}
									toolTipLength={13}
									onItemClick={(value) =>
										onChangeCompositionSelection(
											value,
											index,
											compositionFieldInfoList,
											setCompositionFieldInfoList
										)
									}
									items={compositionNameList}
									selectedItem={obj?.fiberName}
									searchPlaceholder="Composition"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		);
	};
	return (
		<div className={className ? className : 'add-fabric-dropdown p-3 bg-white shadow-24dp'}>
			<div className="row">
				<div className="col-md-12">
					<div className="form-group mr-12">
						<div className="category-select position-relative">
							<label htmlFor="fabricType">Fabric type*</label>
							<FabricSubCategory
								onSelectCallBack={(value) => {
									setFabricDetails({
										...fabricDetails,
										fabricType: value.fabricType,
										fabricTypeWithConstruction: value.value,
										constructionId: value.id
									});
								}}
								type={'INPUT'}
								isEditable={true}
								previousValue={fabricDetails?.fabricTypeWithConstruction}
								isAddNew={checkAnyUserType([
									'ADMIN',
									'INVENTORY_MANAGEMENT',
									'MATERIAL_MANAGEMENT'
								])}
								onAddNew={onAddNew}
								isFabricTypeModify={isFabricTypeModify}
								fabricTypeList={fabricTypeResponse}
								setFabricTypeList={setFabricTypeResponse}
								//selectedFabricTypeToEdit={selectedFabricTypeToEdit}
							/>
							{errors2.fabricTypeError && <p className="error">{errors2.fabricTypeError}</p>}
						</div>
						{isAddNew && (
							<AddNewFabricType
								fabricTypeList={fabricTypeList}
								setIsAddNew={setIsAddNew}
								addNewValue={addNewValue}
								setNewFabricType={setNewFabricType}
								setIsLoading={setIsLoading}
								selectedFabricTypeToEdit={selectedFabricTypeToEdit}
								setSelectedFabricTypeToEdit={setSelectedFabricTypeToEdit}
							/>
						)}
					</div>
				</div>

				<div className="col-md-12">
					<label htmlFor>Composition*</label>
				</div>
				<div className="col-md-12 compositions-list">
					<div className="row">
						{compositionFieldInfoList?.map((obj, index) => compositionRender(obj, index))}
					</div>
				</div>
				<div className="col-md-12 compositonError">
					{errors?.compositionError && <p className="error">{errors.compositionError}</p>}
				</div>
				<div className="col-md-12">
					<div className="form-group">
						<label htmlFor>Weight*</label>
						<div className="d-flex">
							<input
								type="number"
								placeholder="00"
								value={fabricDetails?.weight}
								name="weight"
								onChange={onChangeInputs}
							/>
						</div>
						{errors2.weightError && <p className="error">{errors2.weightError}</p>}
					</div>
				</div>
				<div className="col-md-12">
					<div className="form-group">
						<label htmlFor>Remarks</label>
						<input
							type="text"
							autoComplete="off"
							placeholder="Remarks"
							value={fabricDetails?.description}
							name="description"
							onChange={onChangeInputs}
						/>
					</div>
				</div>
			</div>
			<div className="d-flex justify-content-end gap-2">
				<button className="button text-only text-uppercase" onClick={onClose}>
					Cancel
				</button>
				<button
					disabled={isLoading}
					className="button text-only text-uppercase"
					onClick={onSubmitData}
				>
					Submit
				</button>
			</div>
		</div>
	);
};

export default AddFabricInline;
